import React, { createContext, useContext, useState } from 'react';
import { SalesPartnerBankAccountsModel, SalesPartnerBankAccountsModelData } from 'src/models/salesPartner/SalesPartnerBankAccountModel';
import { SalesPartnerModel, SalesPartnerModelObject } from 'src/models/salesPartner/SalesPartnerModel';
import { SalesPartnerPaymentModel, SalesPartnerPaymentModelData } from 'src/models/salesPartner/SalesPartnerPaymentModel';

import { SalesPartnerBankAccountsServices } from 'src/services/salesPartner/SalesPartnerBankAccountsServices';
import { SalesPartnerPaymentServices } from 'src/services/salesPartner/salesPartnerPaymentServices';
import SalesPartnerService from 'src/services/salesPartner/salesPartnerServices';

interface SalesPartnerContextValue {
  handleGetBankAccountsList: (spId: number, get?: string) => void;
  handleGetSalesPartnerPaymentList: (spId: number, get?: string) => void;
  handleSalesPartner: (data: SalesPartnerModel) => void;
  handleGetAllSalesPartners: (get?: string) => Promise<void>;

  setSalesPartner: (data: SalesPartnerModel) => void;
  setBankAccountsList: (data: SalesPartnerBankAccountsModel[]) => void;
  setBankAccounts: (data: SalesPartnerBankAccountsModel) => void;
  setSalesPartnerPayment: (data: SalesPartnerPaymentModel) => void;
  setAllSalesPartners: (data: SalesPartnerModel[]) => void;


  salesPartner: SalesPartnerModel;
  allSalesPartners: SalesPartnerModel[];
  bankAccountsList: SalesPartnerBankAccountsModel[];
  bankAccounts: SalesPartnerBankAccountsModel;
  salesPartnerPaymentList: SalesPartnerPaymentModel[];
  salesPartnerPayment: SalesPartnerPaymentModel;
}

const SalesPartnerContext = createContext<SalesPartnerContextValue | undefined>(undefined);

export const useSalesPartnerData = () => {
  const context = useContext(SalesPartnerContext);
  if (!context) {
    throw new Error('useSalesPartnerData must be used within a SalesPartnerProvider');
  }
  return context;
};

export const SalesPartnerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [salesPartner, setSalesPartner] = useState<SalesPartnerModel>(
    SalesPartnerModelObject
  );
  const [allSalesPartners, setAllSalesPartners] =
    useState<SalesPartnerModel[]>([]);

  const [bankAccountsList, setBankAccountsList] = useState<SalesPartnerBankAccountsModel[]>([]);
  const [bankAccounts, setBankAccounts] = useState<SalesPartnerBankAccountsModel>(SalesPartnerBankAccountsModelData);

  const [salesPartnerPaymentList, setSalesPartnerPaymentList] = useState<SalesPartnerPaymentModel[]>([]);
  const [salesPartnerPayment, setSalesPartnerPayment] = useState<SalesPartnerPaymentModel>(
    SalesPartnerPaymentModelData
  );

  const handleGetAllSalesPartners = async (get = '') => {

    if (allSalesPartners.length === 0 || get === 'Get') {

      const partners = await SalesPartnerService.getAllSalesPartners();

      const list = Array.isArray(partners)
        ? partners.filter(
          (sp) => sp.isActive === true && sp.isDeleted === false
        )
        : [];

      setAllSalesPartners(list);
    }
  };

  const handleGetBankAccountsList = async (spId: number, get = '') => {
    if (bankAccountsList.length === 0 || get === 'Get') {
      const dataList = await SalesPartnerBankAccountsServices.getAllData(spId);
      const list = Array.isArray(dataList)
        ? dataList
        : [];

      setBankAccountsList(list);
    }
  };

  const handleGetSalesPartnerPaymentList = async (spId: number, get = '') => {
    if (salesPartnerPaymentList.length === 0 || get === 'Get') {
      const dataList = await SalesPartnerPaymentServices.getAllData(spId);
      setSalesPartnerPaymentList(dataList);
    }
  };

  const handleSalesPartner = (data: SalesPartnerModel) => {
    setSalesPartner(data);
    handleGetBankAccountsList(data.id, 'Get');
    handleGetSalesPartnerPaymentList(data.id, 'Get');
  };

  return (
    <SalesPartnerContext.Provider
      value={{
        handleGetAllSalesPartners,
        handleGetBankAccountsList,
        handleGetSalesPartnerPaymentList,
        handleSalesPartner,
        setSalesPartner,
        setBankAccountsList,
        setBankAccounts,
        setSalesPartnerPayment,
        setAllSalesPartners,
        salesPartner,
        allSalesPartners,
        bankAccountsList,
        bankAccounts,
        salesPartnerPaymentList,
        salesPartnerPayment,
      }}
    >
      {children}
    </SalesPartnerContext.Provider>
  );
};
